import { CRUISES_API } from './misc/consts';

import Alpine from 'alpinejs';
import AlpineI18n from 'alpinejs-i18n';
import intersect from '@alpinejs/intersect';
import collapse from '@alpinejs/collapse';

Alpine.plugin(intersect);
Alpine.plugin(collapse);
Alpine.plugin(AlpineI18n);

import AsyncForm from './components/async-form';
import CruiseCalculation from './components/cruise-calculation';
import CruiseCalculationDelegate from './components/cruise-calculation-delegate';
import CruiseDates from './components/cruise-dates';
import CruiseFilter from './components/cruise-filter';
import CurrentOffers from './components/current-offers';
import DelegateCruises from './components/delegate-cruises';
import FaqSearch from './components/faq-search';
import Modal from './components/modal';
import Navbar from './components/navbar';
import NewsSearch from './components/news-search';
import PageNav from './components/page-nav';
import PageNavNav from './components/page-nav-nav';
import Parallax from './components/parallax';
import References from './components/references';
import Scrollbar from './components/scrollbar';
import Tooltip from './components/tooltip';

window.Alpine = Alpine;

Alpine.store('cruises', {
  showStatusBar: false,
  hideRenderedPagination: false,
  filterSet: false,
  searching: false,
  query: '',
  data: [],
  links: {},
  meta: {},
  abortController: null, // Initialize abortController here

  init() {
    setTimeout(() => {
      this.showStatusBar = true;
    }, 600);
  },

  setState(response) {
    this.data = response.data;
    this.links = response.links;
    this.meta = response.meta;
  },

  truncate(text, length, ending = '...') {
    const cleanedText = text.replace(/&nbsp;/g, ' ');

    if (cleanedText.length > length) {
      return cleanedText.substring(0, length - ending.length) + ending;
    } else {
      return cleanedText;
    }
  },

  removeRenderedData() {
    const el = document.getElementById('rendered-cruises-list');

    if (el) {
      el.remove();
    }
  },

  resetFilter() {
    this.filterSet = false;
  },

  setFilter(queryString) {
    // Create a URLSearchParams object from the query string
    const params = new URLSearchParams(queryString);

    // Check if any of the specified parameters exist
    this.filterSet =
      params.has('dates') ||
      params.has('destinations') ||
      params.has('ports') ||
      params.has('ships') ||
      params.has('price');
  },

  get formattedTotal() {
    return this.meta.total.toLocaleString();
  },

  async setSort(sort) {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('sort', sort);
    urlParams.set('page', 1); // Reset page to 1 on sort change

    this.query = `?${urlParams.toString()}`;
    await this.fetch(this.query);
  },

  async goTo(page) {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('page', page);

    this.query = `?${urlParams.toString()}`;
    window.scrollTo(0, 0); // Scroll to the top of the page
    await this.fetch(this.query); // Fetch the new page
  },

  async fetch(query, prefetch = false) {
    this.initializeAbortController();

    const ietfLanguageTag = document.documentElement.getAttribute('data-lang');

    try {
      if (!prefetch) {
        this.showStatusBar = false;
        this.searching = true;
      }
      const response = await this.performFetchRequest(query, ietfLanguageTag);
      await this.processResponse(response, query, prefetch);
    } catch (error) {
      this.handleFetchError(error);
    }
  },

  async processResponse(response, query, prefetch) {
    if (!prefetch && response.ok) {
      const data = await response.json();
      this.updateStateWithData(data, query);
    }
  },

  updateStateWithData(data, query) {
    this.setState(data);
    this.query = query;
    this.searching = false;
    this.updateUrl(query);
    this.hideRenderedPagination = true;
    this.setFilter(query);
    this.updateStatusBarVisibility();
    this.removeRenderedData();
  },

  updateStatusBarVisibility() {
    if (this.hasData) {
      setTimeout(() => {
        this.showStatusBar = true;
      }, 300);
    } else {
      this.showStatusBar = true;
    }
  },

  initializeAbortController() {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
  },

  async performFetchRequest(query, ietfLanguageTag) {
    return fetch(CRUISES_API + query, {
      headers: {
        'Accept-Language': ietfLanguageTag,
      },
      signal: this.abortController.signal,
    });
  },

  updateUrl(query) {
    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${query}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  },

  handleFetchError(error) {
    if (error.name !== 'AbortError') {
      console.error('Error:', error);
    }
    // If it's an AbortError, no need to log as it's expected behavior
  },

  get hasData() {
    return this.data.length > 0;
  },
});

Alpine.data('asyncForm', AsyncForm);
Alpine.data('cruiseCalculation', CruiseCalculation);
Alpine.data('cruiseCalculationDelegate', CruiseCalculationDelegate);
Alpine.data('cruiseDates', CruiseDates);
Alpine.data('cruiseFilter', CruiseFilter);
Alpine.data('currentOffers', CurrentOffers);
Alpine.data('delegateCruises', DelegateCruises);
Alpine.data('faqSearch', FaqSearch);
Alpine.data('modal', Modal);
Alpine.data('navbar', Navbar);
Alpine.data('newsSearch', NewsSearch);
Alpine.data('pageNav', PageNav);
Alpine.data('pageNavNav', PageNavNav);
Alpine.data('parallax', Parallax);
Alpine.data('references', References);
Alpine.data('scrollbar', Scrollbar);
Alpine.data('tooltip', Tooltip);

Alpine.start();
